import * as React from "react";
import { Dropdown, Button, Checkbox } from "antd";
import { WithTranslation, withTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { ViewUserSettingsWithKey } from "../Pages/View";
import { DownOutlined } from "@ant-design/icons";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { projectsDataStore } from "Stores";
import { notificationSuccess } from "utils/libHelpers/AntdNotification";
import { MenuProps } from "antd/lib/menu";
import { ItemType } from "antd/lib/menu/interface";
import { observer } from "mobx-react";
import { viewsStore } from "../Stores/ViewsStore";

interface ViewSettingsProps extends WithTranslation {
  view: API_ViewInformation;
  viewInformation: API_ViewInformation;
  viewId: number;
  reFetch: () => Promise<void>;
}

type ChangeableViewSettings = keyof Pick<
  ViewUserSettingsWithKey,
  | "showIndex"
  | "showLastChange"
  | "showMetadata"
  | "showDescription"
  | "showFilename"
  | "showType"
  | "showStatus"
  | "showLabels"
>;

export interface ViewSettingsState {
  visible: boolean;
  settings: ViewUserSettingsWithKey;
}

export const ViewSettings = withTranslation(I18N_NS.PROJECT)(
  observer(
    class extends React.Component<ViewSettingsProps, ViewSettingsState> {
      readonly state = { visible: false, settings: null };

      componentDidUpdate(prevProps: ViewSettingsProps) {
        if (
          JSON.stringify(prevProps.view).length !== JSON.stringify(this.props.view).length ||
          JSON.stringify(prevProps.viewInformation).length !== JSON.stringify(this.props.viewInformation).length
        ) {
          this.setState({ settings: this.makeViewSettings() });
        }
      }

      componentDidMount() {
        this.setState({ settings: this.makeViewSettings() });
      }

      updateSettings = async (newSettings: API_ViewUserSettings) => {
        try {
          const { t } = this.props;
          const { viewId, reFetch } = this.props;
          await API.editData(API_URL.VIEWS.saveUserSettingsForView(projectsDataStore.projectId, viewId), newSettings);
          notificationSuccess(t("Success"), t("Settings saved"));
          await reFetch();
          const settings = this.makeViewSettings();
          this.setState({ settings: settings });
        } catch (error) {
          throw error;
        }
      };

      getLabelByLanguage = (item: API_DataType) => {
        return this.props.i18n.language === "de" ? item.nameDe : item.nameEn;
      };

      makeViewSettings = (viewData?: API_ViewInformation): ViewUserSettingsWithKey => {
        const view = viewData || this.props.view;
        if (!view) {
          return null;
        }
        return {
          id: view.id,
          showIndex: view.showIndex,
          showType: view.showType,
          showFilename: view.showFilename,
          showDescription: view.showDescription,
          showLastChange: view.showLastChange,
          showStatus: view.showStatus,
          showMetadata:
            viewsStore.viewInformation.metadata?.map((item) => ({
              show: item.show,
              metadataId: item.definitionId,
              key: item.key,
            })) || [],
          showLabels:
            viewsStore.viewInformation.typesWithLabels?.map((item) => ({
              show: item.show,
              labelTypeId: item.id,
              key: `labelType_${item.id}`,
              label: this.getLabelByLanguage(item),
            })) || [],
        };
      };

      getPropertyByKey = (key: string): ChangeableViewSettings => {
        if (key === "show_index") {
          return "showIndex";
        } else if (key === "show_type") {
          return "showType";
        } else if (key === "show_name") {
          return "showFilename";
        } else if (key === "show_description") {
          return "showDescription";
        } else if (key === "show_status") {
          return "showStatus";
        } else if (key === "show_last_change") {
          return "showLastChange";
        } else if (key.indexOf("metadata_") === 0) {
          return "showMetadata";
        } else if (key.indexOf("labelType_") === 0) {
          return "showLabels";
        }
        throw new Error('unsupported "key" value');
      };

      handleMenuClick: MenuProps["onClick"] = ({ key, domEvent }) => {
        domEvent.preventDefault();
        domEvent.stopPropagation();
        const propToChange = this.getPropertyByKey(key as string);
        const settings = Object.assign({}, this.makeViewSettings());
        // toggle "show" for certain metadata
        if (propToChange === "showMetadata") {
          // should return metadataId. check out Menu.Item "key" property
          const metadataId = parseInt((key as string).replace("metadata_", ""), 10);
          const metadataIdxChanged = settings.showMetadata.findIndex((item) => item.metadataId === metadataId);
          if (metadataIdxChanged !== -1) {
            settings.showMetadata[metadataIdxChanged] = {
              ...settings.showMetadata[metadataIdxChanged],
              // toggle metadata display
              show: !settings.showMetadata[metadataIdxChanged].show,
            };
          }
        } else if (propToChange === "showLabels") {
          const labelTypeId = parseInt((key as string).replace("labelType_", ""), 10);
          const labelTypeIdxChanged = settings.showLabels.findIndex((item) => item.labelTypeId === labelTypeId);
          console.log("labelTypeIdxChanged", labelTypeIdxChanged);
          if (labelTypeIdxChanged !== -1) {
            settings.showLabels[labelTypeIdxChanged] = {
              ...settings.showLabels[labelTypeIdxChanged],
              // toggle metadata display
              show: !settings.showLabels[labelTypeIdxChanged].show,
            };
          }
        } else {
          // otherwise toggle show for certain flag
          settings[propToChange] = !settings[propToChange];
        }
        // remove ui-specific "key" prop before saving
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (settings as any).showMetadata = settings.showMetadata.map((item) => ({
          metadataId: item.metadataId,
          show: item.show,
        }));
        settings.showLabels = settings.showLabels?.map((item) => ({
          labelTypeId: item.labelTypeId,
          show: item.show,
        }));
        this.updateSettings(settings);
      };

      handleVisibleChange = (flag, { source }) => {
        if (source === "trigger") {
          this.setState({ visible: flag });
        }
      };

      get menuItems(): ItemType[] {
        const { t } = this.props;
        const { settings } = this.state;
        return [
          {
            key: "show_name",
            label: <Checkbox checked={settings.showFilename}>{t("Show filename")}</Checkbox>,
          },
          {
            key: "show_status",
            label: <Checkbox checked={settings.showStatus}>{t("Show file Status")}</Checkbox>,
          },
          {
            key: "show_index",
            label: <Checkbox checked={settings.showIndex}>{t("Show index")}</Checkbox>,
          },
          {
            key: "show_type",
            label: <Checkbox checked={settings.showType}>{t("Show type")}</Checkbox>,
          },
          {
            key: "show_last_change",
            label: <Checkbox checked={settings.showLastChange}>{t("Show last change")}</Checkbox>,
          },
          {
            key: "show_description",
            label: <Checkbox checked={settings.showDescription}>{t("Show description")}</Checkbox>,
          },
          {
            key: "metadata",
            label: t("Metadata"),
            children: settings.showMetadata.map((item) => {
              const key = `metadata_${item.metadataId}`;
              return {
                key: key,
                label: (
                  <div
                    onClick={(e) => {
                      this.handleMenuClick({
                        key: key,
                        keyPath: ["metadata", key],
                        item: null,
                        itemData: { key },
                        domEvent: e,
                      });
                    }}
                  >
                    <Checkbox checked={item.show}>{item.key}</Checkbox>
                  </div>
                ),
              };
            }),
          },
          settings.showLabels && {
            key: "typesWithLabels",
            label: t("Labels"),
            children: settings.showLabels.map((item) => {
              return {
                key: item.key,
                label: (
                  <div
                    onClick={(e) => {
                      this.handleMenuClick({
                        key: item.key,
                        keyPath: ["typesWithLabels", item.key],
                        item: null,
                        itemData: { key: item.key },
                        domEvent: e,
                      });
                    }}
                  >
                    <Checkbox checked={item.show}>{item.label}</Checkbox>
                  </div>
                ),
              };
            }),
          },
        ].filter(Boolean);
      }

      render() {
        const { t } = this.props;
        const { settings } = this.state;
        if (!settings) {
          return null;
        }

        return (
          <Dropdown
            menu={{
              items: this.menuItems,
              onClick: this.handleMenuClick,
              selectable: false,
            }}
            onOpenChange={this.handleVisibleChange}
            open={this.state.visible}
            trigger={["click"]}
          >
            <Button>
              {t("Settings", { ns: I18N_NS.GENERAL })} <DownOutlined />
            </Button>
          </Dropdown>
        );
      }
    }
  )
);
